import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface ColorGridProps {
  onColorSelect?: (color: string, x: number, y: number) => void;
  selectedColors?: { x: number; y: number; color: string; playerId?: string }[];
  isSelectable?: boolean;
  showTarget?: boolean;
  targetColor?: string | null;
  targetX?: number;
  targetY?: number;
  gameMode?: string;
  currentPlayerId?: string | null;
}

export function ColorGrid({ 
  onColorSelect, 
  selectedColors = [], 
  isSelectable = true,
  showTarget = false,
  targetColor,
  targetX,
  targetY,
  gameMode,
  currentPlayerId
}: ColorGridProps) {
  const [colorPalette, setColorPalette] = useState<string[][]>([]);
  // Check if mobile for styling purposes only
  const [isMobile, setIsMobile] = React.useState(false);
  
  // Load color palette from JSON
  useEffect(() => {
    fetch('/hues_palette.json')
      .then(res => res.json())
      .then(data => setColorPalette(data))
      .catch(err => console.error('Failed to load color palette:', err));
  }, []);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Match Hues and Cues board dimensions based on palette
  const GRID_WIDTH = 30;
  const GRID_HEIGHT = 16;
  
  // Get color from palette
  const getColor = (x: number, y: number): string => {
    if (!colorPalette.length || y >= colorPalette.length || x >= colorPalette[y].length) {
      return '#cccccc'; // Fallback color
    }
    return colorPalette[y][x];
  };

  const getSelectionInfo = (x: number, y: number): { isSelected: boolean; playerId?: string } => {
    const selection = selectedColors.find(sel => sel.x === x && sel.y === y);
    return {
      isSelected: !!selection,
      playerId: selection?.playerId
    };
  };
  
  // Define player colors for speed mode - extended palette for up to 20 players
  const getPlayerColor = (playerId?: string): string => {
    if (!playerId) return 'orange';
    const playerColors = [
      'orange',     // Player 1
      'lightning',  // Player 2
      'green',      // Player 3
      'purple',     // Player 4
      'red',        // Player 5
      'yellow',     // Player 6
      'pink',       // Player 7
      'indigo',     // Player 8
      'cyan',       // Player 9
      'amber',      // Player 10
      'lime',       // Player 11
      'rose',       // Player 12
      'violet',     // Player 13
      'emerald',    // Player 14
      'fuchsia',    // Player 15
      'teal',       // Player 16
      'sky',        // Player 17
      'blue',       // Player 18
      'slate',      // Player 19
      'gray'        // Player 20
    ];
    const playerIndex = parseInt(playerId) % playerColors.length;
    return playerColors[playerIndex];
  };

  if (!colorPalette.length) {
    return <div className="bg-gray-900 p-4 rounded-lg text-gray-400">Loading color palette...</div>;
  }

  return (
    <div className={`color-grid ${isMobile ? 'max-w-[90vw] max-h-[60vh] overflow-auto' : 'inline-block'} bg-gray-900 p-2 rounded-lg`}>
      <div 
        className="grid gap-0.5"
        style={{
          gridTemplateColumns: `repeat(${GRID_WIDTH}, ${isMobile ? '2rem' : '1.75rem'})`,
          gridTemplateRows: `repeat(${GRID_HEIGHT}, ${isMobile ? '2rem' : '1.75rem'})`,
        }}
      >
        {Array.from({ length: GRID_HEIGHT }, (_, y) =>
          Array.from({ length: GRID_WIDTH }, (_, x) => {
            const color = getColor(x, y);
            const isTarget = showTarget && targetX === x && targetY === y;
            const selectionInfo = getSelectionInfo(x, y);
            const playerColor = getPlayerColor(selectionInfo.playerId);
            
            // Determine ring color based on game mode and player
            let ringColorClass = '';
            if (selectionInfo.isSelected) {
              if (gameMode === 'speed' && selectionInfo.playerId !== undefined) {
                // Use player-specific colors in speed mode
                const colorMap: Record<string, string> = {
                  'orange': 'ring-orange-500',
                  'lightning': 'ring-lightning-500',
                  'green': 'ring-green-500',
                  'purple': 'ring-purple-500',
                  'red': 'ring-red-500',
                  'yellow': 'ring-yellow-500',
                  'pink': 'ring-pink-500',
                  'indigo': 'ring-indigo-500',
                  'cyan': 'ring-cyan-500',
                  'amber': 'ring-amber-500',
                  'lime': 'ring-lime-500',
                  'rose': 'ring-rose-500',
                  'violet': 'ring-violet-500',
                  'emerald': 'ring-emerald-500',
                  'fuchsia': 'ring-fuchsia-500',
                  'teal': 'ring-teal-500',
                  'sky': 'ring-sky-500',
                  'blue': 'ring-blue-500',
                  'slate': 'ring-slate-500',
                  'gray': 'ring-gray-500'
                };
                ringColorClass = colorMap[playerColor] || 'ring-orange-500';
              } else {
                // Default orange for other modes
                ringColorClass = 'ring-orange-500';
              }
            }
            
            return (
              <motion.button
                key={`${x}-${y}`}
                className={`
                  color-cell transition-all w-full h-full relative
                  ${isSelectable ? 'cursor-pointer' : 'cursor-default'}
                  ${selectionInfo.isSelected ? `ring-2 ${ringColorClass} scale-110 z-20` : ''}
                  ${isTarget ? 'ring-4 ring-lightning-500' : ''}
                  ${!isMobile && isSelectable ? 'hover:scale-125 hover:z-10' : ''}
                `}
                style={{ backgroundColor: color }}
                onClick={() => isSelectable && onColorSelect?.(color, x, y)}
                whileHover={isSelectable && !isMobile ? { scale: 1.25 } : {}}
                whileTap={isSelectable ? { scale: 0.85 } : {}}
                disabled={!isSelectable}
                aria-label={`Color at position ${x}, ${y}`}
              >
                {/* Show player number in speed mode */}
                {gameMode === 'speed' && selectionInfo.isSelected && selectionInfo.playerId !== undefined && (
                  <div className={`absolute inset-0 flex items-center justify-center pointer-events-none`}>
                    <span className="text-white text-xs font-bold bg-black bg-opacity-50 rounded-full w-5 h-5 flex items-center justify-center">
                      {parseInt(selectionInfo.playerId) + 1}
                    </span>
                  </div>
                )}
              </motion.button>
            );
          })
        ).flat()}
      </div>
    </div>
  );
}